import React from 'react';
import { ILinkedEmail, IEmailServer, EmailServerIntegrationType } from '@/entities/admin-app/email';
import { themeColors } from '@/theme/colors';
import { Button, SimpleGrid, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconPlus } from '@tabler/icons';
import { useTranslation } from 'react-i18next';
import { useStyles } from './styles';
import { LinkEmailsModals } from '@/containers/pages/user/components/sections/linked-emails/components/LinkEmailsModals';
import { useLinkedEmail } from '@hooks/users/useLinkedEmail';
import { EmailItem } from '@/containers/pages/user/components/sections/linked-emails/components/EmailItem';

export interface ILinkedEmailsProps {
  form: UseFormReturnType<any>;
  sectionFormKey: string;
  listLinkedEmails: ILinkedEmail[] | null;
  isCreateUserPage?: boolean;
  filialId?: number | null;
  currentId?: number;
  editUser: boolean;
  refetchUserInfo?(): void;
}

const getIntegrationTypeLabel = (
  type: EmailServerIntegrationType,
  t: (key: string) => string
): string => {
  switch (type) {
    case EmailServerIntegrationType.Ssh:
      return t('profile.integrationTypeSsh');
    case EmailServerIntegrationType.Api:
      return t('profile.integrationTypeApi');
    default:
      return t('profile.integrationTypeOther');
  }
};

interface IServerGroupProps {
  servers: Array<{ server: IEmailServer; emails: ILinkedEmail[] }>;
  form: UseFormReturnType<any>;
  sectionFormKey: string;
  currentId?: number;
  listEmails: ILinkedEmail[];
  editUser: boolean;
  refetchUserInfo?: () => void;
}

const ServerGroup: React.FC<IServerGroupProps> = ({
  servers,
  form,
  sectionFormKey,
  currentId,
  listEmails,
  editUser,
  refetchUserInfo
}) => {
  const { t } = useTranslation();

  return (
    <>
      {servers?.map((serverGroup) => (
        <React.Fragment key={serverGroup.server.Id}>
          <Text fw="500" mt={16} fz={15} c="#000">
            {getIntegrationTypeLabel(serverGroup.server.IntegrationType, t)}{' '}
            {serverGroup.server.Name}
          </Text>
          {serverGroup.emails.map((email, index) => (
            <EmailItem
              email={email}
              key={index}
              form={form}
              sectionFormKey={sectionFormKey}
              currentId={currentId}
              listEmails={listEmails}
              editUser={editUser}
              refetchUserInfo={refetchUserInfo}
            />
          ))}
        </React.Fragment>
      ))}
    </>
  );
};

export const LinkedListEmails = (props: ILinkedEmailsProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { form, sectionFormKey, filialId, currentId, editUser, refetchUserInfo } = props;

  const {
    opened,
    isLoading,
    isSuccess,
    isOpenedCreateModal,
    visibleCreateButton,
    listEmails,
    handleAddEmail,
    openCreateModal,
    closeCreateModal,
    open,
    close
  } = useLinkedEmail(props);

  // Группируем серверы по типу интеграции
  const groupedServers = React.useMemo(() => {
    // Получаем уникальные серверы
    const uniqueServers = Array.from(
      new Map(
        listEmails
          .map((listEmail) => listEmail.EmailServer)
          .filter((emailServer) => emailServer !== null)
          .map((emailServer) => [emailServer.Id, emailServer])
      ).values()
    );

    // Группируем по типу
    return uniqueServers.reduce((acc, server) => {
      // Определяем тип интеграции
      let type: 'ssh' | 'api' | 'other' = 'other';

      if (server.IntegrationType === EmailServerIntegrationType.Ssh) {
        type = 'ssh';
      } else if (server.IntegrationType === EmailServerIntegrationType.Api) {
        type = 'api';
      }

      // Находим все email для этого сервера
      const emails = listEmails.filter((email) => email.EmailServer?.Id === server.Id);

      if (!acc[type]) {
        acc[type] = [];
      }

      acc[type].push({
        server,
        emails
      });

      return acc;
    }, {} as Record<'ssh' | 'api' | 'other', Array<{ server: IEmailServer; emails: ILinkedEmail[] }>>);
  }, [listEmails]);

  return (
    <>
      <>
        <SimpleGrid cols={{ base: 1, xs: 2 }} w="100%">
          <Button
            className={classes.addBtn}
            size="sm"
            variant="outline"
            fw="lighter"
            rightSection={<IconPlus color={themeColors.brandGray[0]} />}
            onClick={open}
          >
            {t('profile.linkAccount')}
          </Button>
        </SimpleGrid>
        {visibleCreateButton && (
          <SimpleGrid cols={{ base: 1, xs: 2 }} mt={16} w="100%">
            <Button
              className={classes.addBtn}
              size="sm"
              variant="outline"
              fw="lighter"
              disabled={!filialId}
              rightSection={<IconPlus color={themeColors.brandGray[0]} />}
              onClick={openCreateModal}
            >
              {t('profile.createNewAccount')}
            </Button>
          </SimpleGrid>
        )}
      </>
      {listEmails.length !== 0 && (
        <Text className={classes.accountName} fw="500" mt={16} fz={15} c="#000">
          {t('profile.mailServers')}
        </Text>
      )}
      <div className={classes.listWrapper}>
        <ServerGroup
          servers={groupedServers.ssh || []}
          form={form}
          sectionFormKey={sectionFormKey}
          currentId={currentId}
          listEmails={listEmails}
          editUser={editUser}
          refetchUserInfo={refetchUserInfo}
        />
        <ServerGroup
          servers={groupedServers.api || []}
          form={form}
          sectionFormKey={sectionFormKey}
          currentId={currentId}
          listEmails={listEmails}
          editUser={editUser}
          refetchUserInfo={refetchUserInfo}
        />
        <ServerGroup
          servers={groupedServers.other || []}
          form={form}
          sectionFormKey={sectionFormKey}
          currentId={currentId}
          listEmails={listEmails}
          editUser={editUser}
          refetchUserInfo={refetchUserInfo}
        />
      </div>
      <Text mt={16} fz={14} c="#667085">
        {t('profile.infoDescription')}
      </Text>
      <LinkEmailsModals
        isOpenedCreateModal={isOpenedCreateModal}
        filialId={filialId}
        editUser={editUser}
        isLoading={isLoading}
        isSuccess={isSuccess}
        close={close}
        opened={opened}
        closeCreateModal={closeCreateModal}
        handleAddEmail={handleAddEmail}
      />
    </>
  );
};
